import axios from 'axios'
import { UseQueryOptions } from '@tanstack/vue-query'
import { computed, unref } from 'vue'

import { useLocalTripSettings } from './trip-settings'
import { useContext } from '@/composables/context'
import { useQueryClient, useQuery, useMutation } from '@/composables/vue-query'
import { eventModule, tripModule } from '@/store'
import type {
  DateRange,
  TripSummary,
  Trip,
  ValidatePinCodeResponse,
  CreateTripResponse,
  JoinTripResponse,
  ItineraryItem,
  ItineraryTrip,
} from '@/types/trip'
import { PendingTrip } from '@/types/onboarding'
import type { MaybeRef } from '@/types/types'
import { DistanceUnitMap } from '@/utils/enums'

export interface UseTripListOptions {
  page: MaybeRef<number>
  search: MaybeRef<string>
  filters?: MaybeRef<Record<string, string[]>>
}

export const tripQueryKeys = {
  all: () => ['trips'] as const,
  lists: () => [...tripQueryKeys.all(), 'list'] as const,
  list: () => [...tripQueryKeys.lists()] as const,
  listAdvanced: (options: UseTripListOptions) =>
    [...tripQueryKeys.lists(), 'advanced', options] as const,
  archive: () => [...tripQueryKeys.all(), 'archive'] as const,
  summaries: () => [...tripQueryKeys.all(), 'summary'] as const,
  summary: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.summaries(), tripId] as const,
  itineraries: () => [...tripQueryKeys.all(), 'itineraries'] as const,
  itinerary: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.itineraries(), tripId] as const,
  details: () => [...tripQueryKeys.all(), 'details'] as const,
  detail: (tripId: MaybeRef<string>) =>
    [...tripQueryKeys.all(), 'details', tripId] as const,
}

export function useAdvancedTripList(options: UseTripListOptions) {
  const { page, search } = options

  const { $axios } = useContext()

  return useQuery({
    queryKey: tripQueryKeys.listAdvanced(options),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page: unref(page),
        query: unref(search),
      }

      const rawFilters = unref(options.filters)

      if (rawFilters?.date && rawFilters.date.length > 0) {
        params.date = rawFilters.date.join(',')
      }

      const { data } = await $axios.get(`/api/trips/advanced`, {
        params,
      })
      return data
    },
    keepPreviousData: true,
  })
}

export function useCreateTrip() {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (newTrip: PendingTrip) => {
      const { data } = await $axios.post<CreateTripResponse>('/api/trips', {
        ...newTrip,
      })

      return data
    },
    onSuccess: () => {
      eventModule.newMessage('success.trip.create')
      queryClient.invalidateQueries({ queryKey: tripQueryKeys.lists() })
    },
    onError: () => {
      eventModule.newError('error.trip.create')
    },
  })
}

export interface TripUpdate {
  date?: {
    startDate: DateRange
    endDate?: DateRange
  } | null
  name?: string
}

export function useUpdateTrip(tripId: MaybeRef<string>) {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: TripUpdate) => {
      const { data } = await $axios.put<{ trip: Trip }>(
        `/api/trips/${unref(tripId)}`,
        payload,
      )
      return data.trip
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tripQueryKeys.itinerary(data.id),
        }),
        queryClient.invalidateQueries({
          queryKey: tripQueryKeys.summary(data.id),
        }),
      ])
    },
  })
}

export function useDeleteTrip() {
  const { $axios } = useContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (tripId: string) => {
      await $axios.delete(`/api/trips/${tripId}`)
    },
    onSuccess: async () => {
      eventModule.newMessage('success.trip.delete')
      await queryClient.invalidateQueries({ queryKey: tripQueryKeys.all() })
    },
  })
}

export function useTripSummary(tripId: MaybeRef<string>) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: tripQueryKeys.summary(tripId),
    queryFn: async () => {
      const {
        data: { summary },
      } = await $axios.get<{ summary: TripSummary }>(
        `/api/trips/${unref(tripId)}/summary`,
      )
      return summary
    },
  })
}

interface TripItineraryResponse {
  trip: ItineraryTrip
  itinerary: ItineraryItem[]
}

export function useTripItinerary(
  tripId: MaybeRef<string>,
  options?: UseQueryOptions<TripItineraryResponse>,
) {
  const { $axios } = useContext()
  const { tripSettings } = useLocalTripSettings(tripId)

  return useQuery({
    queryKey: tripQueryKeys.itinerary(tripId),
    queryFn: async () => {
      const id = unref(tripId)

      const dm =
        DistanceUnitMap[
          tripSettings.value.distanceUnit as keyof typeof DistanceUnitMap
        ] ?? 0

      const { data } = await $axios.get<{
        data: { trip: ItineraryTrip; itinerary: ItineraryItem[] }
      }>(`/api/trips/${id}/itinerary`, {
        params: { dm },
      })
      return data.data
    },
    ...options,
  })
}

export function useTripList(enabled: MaybeRef<boolean> = true) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: tripQueryKeys.lists(),
    queryFn: async () => {
      const {
        data: { trips, hasArchive },
      } = await $axios.get<{ trips: Trip[]; hasArchive: boolean }>(
        `/api/trips`,
        {},
      )
      tripModule.updateHasArchive(hasArchive)
      return trips
    },
    enabled: unref(enabled),
  })
}

export interface TripArchiveResponse {
  hasArchive: boolean
  trips: Trip[]
}

export function useTripArchiveList(enabled: MaybeRef<boolean> = true) {
  const { $axios } = useContext()

  return useQuery({
    queryKey: tripQueryKeys.archive(),
    queryFn: async () => {
      const {
        data: { trips },
      } = await $axios.get<TripArchiveResponse>(`/api/trips/archive`, {})
      return trips
    },
    enabled: unref(enabled),
  })
}

export function useJoinTrip() {
  const { $axios } = useContext()

  return useMutation({
    mutationFn: async (pinCode: string) => {
      const { data } = await $axios.put<JoinTripResponse>(`/api/joinTrip`, {
        pinCode,
      })

      return data
    },
    onError() {
      eventModule.newError('error.trip.join')
    },
  })
}

export interface UseTripDetailsOptions {
  pinCode: MaybeRef<string>
  queryOptions?: UseQueryOptions<boolean>
}

export function useValidatePinCode() {
  const { $axios } = useContext()
  const validatePinCode = async (pinCode: string) => {
    try {
      const { data } = await $axios.get<ValidatePinCodeResponse>(
        `/api/joinTrip/${unref(pinCode)}/validate`,
      )
      return 'available' in data
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        eventModule.newError('error.generic')
        return false
      }
      const errorCode = error.response?.data.code ?? -1

      if (errorCode === 11011201 || errorCode === 11011102) {
        eventModule.newError('error.trip.validatePinCode')
      } else {
        eventModule.newError('error.generic')
      }

      return false
    }
  }
  return {
    validatePinCode,
  }
}

export function useSelectedTrip() {
  const trip = computed(() => {
    if (!tripModule.trip) {
      throw new Error('No trip selected')
    }
    return tripModule.trip
  })
  return trip
}
